import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import styles from "./DateFilter.module.css";

const DEFAULT_PRESET_FILTERS = [
  { label: "Today", days: 0 },
  { label: "This Week", days: 7 },
  { label: "This Month", days: 30 },
  { label: "Last 3 Months", days: 90 },
];

const DateFilterDropdown = ({
  onDateChange,
  defaultFilter = "Last 3 Months",
  className = "",
  dropdownButtonClassname = "",
  presetFilters = DEFAULT_PRESET_FILTERS,
}) => {
  const [fromDate, setFromDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [selectedFilter, setSelectedFilter] = useState(defaultFilter);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const predefinedFilters = presetFilters;

  const minSelectableDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 2);
    return d;
  })();

  const formatDateRange = () => {
    if (!fromDate || !toDate) {
      return "Select Period";
    }

    const formatDate = (date) => {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const d = new Date(date);
      return `${d.getDate()} ${months[d.getMonth()]}`;
    };

    // If same day, just show one date
    if (fromDate.toDateString() === toDate.toDateString()) {
      return formatDate(fromDate);
    }

    // If same year, show range without year
    if (fromDate.getFullYear() === toDate.getFullYear()) {
      return `${formatDate(fromDate)} - ${formatDate(toDate)}`;
    }

    // If different years, include years
    return `${formatDate(fromDate)} ${fromDate.getFullYear()} - ${formatDate(
      toDate
    )} ${toDate.getFullYear()}`;
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (selectedFilter) {
      dateFilter();
    }
  }, [selectedFilter]);

  const calculateDateRange = (daysAgo, isSingleDay) => {
    const currentDate = new Date();
    const from = new Date();

    if (isSingleDay) {
      from.setDate(from.getDate() - daysAgo);
      from.setHours(0, 0, 0, 0);
      const to = new Date(from);
      to.setHours(23, 59, 59, 999);
      return { from, to };
    }

    if (daysAgo === 0) {
      from.setHours(0, 0, 0, 0);
      currentDate.setHours(23, 59, 59, 999);
    } else {
      from.setDate(currentDate.getDate() - (daysAgo - 1));
      // Set to start of day for 'from' date
      from.setHours(0, 0, 0, 0);
      // Set to end of day for 'to' date
      currentDate.setHours(23, 59, 59, 999);
    }

    return { from, to: currentDate };
  };

  const dateFilter = () => {
    if (selectedFilter) {
      const filter = predefinedFilters.find((f) => f.label === selectedFilter);
      if (filter) {
        const { from, to } = calculateDateRange(filter.days, filter.isSingleDay);
        setFromDate(from);
        setToDate(to);
        onDateChange({
          fromDate: from.toISOString(),
          toDate: to.toISOString(),
          selectedFilter: filter.label,
        });
      }
    }
  };

  const handleCustomDate = ({ date, type }) => {
    setSelectedFilter("Custom");
    if (type === "toDate") {
      const endOfDay = new Date(date.setHours(23, 59, 59, 999));
      setToDate(endOfDay);
      onDateChange({
        fromDate: fromDate.toISOString(),
        toDate: endOfDay.toISOString(),
        selectedFilter: "Custom",
      });
    } else if (type === "fromDate") {
      const startOfDay = new Date(date.setHours(0, 0, 0, 0));
      setFromDate(startOfDay);
      onDateChange({
        fromDate: startOfDay.toISOString(),
        toDate: toDate.toISOString(),
        selectedFilter: "Custom",
      });
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleSelect = (filter) => {
    setSelectedFilter(filter.label);
    setDropdownOpen(false);
  };

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setDropdownOpen(false);
    }
  };

  return (
    <div className={`${styles.filterDropdown} ${className}`} ref={dropdownRef}>
      <button
        className={`${styles.dropdownBtn} ${dropdownButtonClassname} dropdown-toggle`}
        type="button"
        onClick={toggleDropdown}
      >
        <span className={styles.dateRangeText}>
          {selectedFilter && selectedFilter.toLowerCase() !== "custom"
            ? selectedFilter
            : formatDateRange()}
        </span>
      </button>

      <div
        className={`${styles.dropdownMenu} ${dropdownOpen ? styles.show : ""}`}
      >
        {predefinedFilters.map((filter) => (
          <div
            key={filter.label}
            className={`${styles.filterItem} ${
              selectedFilter === filter.label ? styles.active : ""
            } mx-2`}
            onClick={() => handleSelect(filter)}
          >
            {filter.label}
          </div>
        ))}

        <div className={styles.divider} />

        <div className={styles.customDateSection}>
          <div className={`mb-2 ${styles.customDateTitle}`}>Custom Range</div>

          <div className={styles.datePickerWrapper}>
            <div className={styles.datePickerLabel}>From</div>
            <DatePicker
              selected={fromDate}
              onChange={(date) => handleCustomDate({ date, type: "fromDate" })}
              minDate={minSelectableDate}
              maxDate={toDate}
              className={styles.datePicker}
              dateFormat="dd MMMM yyyy"
            />
          </div>

          <div className={styles.datePickerWrapper}>
            <div className={styles.datePickerLabel}>To</div>
            <DatePicker
              selected={toDate}
              onChange={(date) => handleCustomDate({ date, type: "toDate" })}
              minDate={fromDate < minSelectableDate ? minSelectableDate : fromDate}
              maxDate={new Date()}
              className={styles.datePicker}
              dateFormat="dd MMMM yyyy"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateFilterDropdown;
